/**
 * Allowed HTML tags for rich text: b, br, u, i, em. strong is normalized to b. span and all other tags are stripped.
 * Use for paragraphs, titles, accordion content, and any user-editable text.
 */
const ALLOWED_TAGS = /^(?:b|strong|br|u|i|em)$/;

export function sanitizeParagraphHtml(html: string): string {
  if (typeof html !== "string" || !html.trim()) return "";
  // Strip any tag not in whitelist (including span)
  let out = html.replace(/<\/?([a-z][a-z0-9]*)(?:\s[^>]*)?\/?>/gi, (match, tagName) => {
    const tag = (tagName || "").toLowerCase();
    if (ALLOWED_TAGS.test(tag)) return match;
    return "";
  });
  // Normalize allowed tags: strip attributes; strong -> b
  out = out
    .replace(/<(strong)(?:\s[^>]*)?>/gi, "<b>")
    .replace(/<\/(strong)>/gi, "</b>")
    .replace(/<(b)(?:\s[^>]*)?>/gi, "<b>")
    .replace(/<\/(b)>/gi, "</b>")
    .replace(/<(br)(?:\s[^>]*)?\/?>/gi, "<br>")
    .replace(/<(u)(?:\s[^>]*)?>/gi, "<u>")
    .replace(/<\/(u)>/gi, "</u>")
    .replace(/<(i)(?:\s[^>]*)?>/gi, "<i>")
    .replace(/<\/(i)>/gi, "</i>")
    .replace(/<(em)(?:\s[^>]*)?>/gi, "<em>")
    .replace(/<\/(em)>/gi, "</em>");
  // Max 1 empty line: collapse 3+ <br> to 2
  out = out.replace(/(<br\s*\/?>)\s*(<br\s*\/?>)(\s*<br\s*\/?>)+/gi, "$1$2");
  return out;
}

/** Split plain text by empty lines into paragraphs; each paragraph's newlines become <br>. */
export function splitPlainTextIntoParagraphs(text: string): string[] {
  if (typeof text !== "string" || !text.trim()) return [];
  return text
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => block.replace(/\n/g, "<br>"));
}

/** Join HTML paragraphs into plain text: <br> → newline, paragraph boundary = blank line. */
export function paragraphsToPlainText(paragraphs: string[]): string {
  if (!Array.isArray(paragraphs) || paragraphs.length === 0) return "";
  return paragraphs
    .map((p) => (typeof p === "string" ? p : "").replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "").trim())
    .join("\n\n");
}

const PARA_SEP = "<br><br>";

/** Join paragraphs into one HTML string for the Raw editor (split by double <br>). */
export function paragraphsToRawHtml(paragraphs: string[]): string {
  if (!Array.isArray(paragraphs) || paragraphs.length === 0) return "";
  return paragraphs.map((p) => (typeof p === "string" ? p.trim() : "")).join(PARA_SEP);
}

/** Split Raw editor HTML by double <br> into paragraphs; sanitize each. */
export function rawHtmlToParagraphs(html: string): string[] {
  if (typeof html !== "string" || !html.trim()) return [];
  const parts = html.split(/(?:<br\s*\/?>\s*){2,}/i).map((s) => s.trim()).filter(Boolean);
  return parts.map((p) => sanitizeParagraphHtml(p));
}

/** Keys that may contain rich HTML; sanitize at save time. Includes accordion item titles, section headings, etc. */
const RICH_TEXT_KEYS = new Set(["title", "text", "body", "subtitle", "panelTitle", "description", "introParagraph"]);

function sanitizeContentValue(value: unknown): unknown {
  if (typeof value === "string") return sanitizeParagraphHtml(value);
  if (Array.isArray(value)) return value.map(sanitizeContentValue);
  if (value !== null && typeof value === "object") return sanitizeContentObject(value as Record<string, unknown>);
  return value;
}

function sanitizeContentObject(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key === "paragraphs" && Array.isArray(value)) {
      out[key] = value.map((item) => (typeof item === "string" ? sanitizeParagraphHtml(item) : item));
    } else if (RICH_TEXT_KEYS.has(key) && typeof value === "string") {
      out[key] = sanitizeParagraphHtml(value);
    } else {
      out[key] = sanitizeContentValue(value);
    }
  }
  return out;
}

/**
 * Sanitize all rich-text fields in page components at save time.
 * - Paragraphs arrays (e.g. TextBlock, AccordionSection items): each string is sanitized; <span> and disallowed tags are stripped.
 * - Title/text/body/subtitle/panelTitle/description/introParagraph: sanitized (including accordion item titles).
 * - Nested structures (e.g. content.items[].title, content.items[].paragraphs) are recursed into.
 */
export function sanitizePageComponentsForSave(components: unknown[]): unknown[] {
  if (!Array.isArray(components)) return components;
  return components.map((comp) => {
    if (comp === null || typeof comp !== "object") return comp;
    const c = comp as { componentType?: string; key?: string; content?: unknown };
    if (c.content === null || typeof c.content !== "object") return comp;
    let content = sanitizeContentObject(c.content as Record<string, unknown>);
    if (c.componentType === "SectionHeading" && typeof content.text === "string") {
      content = { ...content, text: content.text.replace(/\r?\n/g, " ").trim() };
    }
    // ArticleHero: do not persist shareLabel/shareOptions (fixed in renderer: "Partager", all social on)
    if (c.componentType === "ArticleHero" && content && typeof content === "object") {
      const { shareLabel: _sl, shareOptions: _so, ...rest } = content as Record<string, unknown>;
      content = rest;
    }
    return { ...c, content };
  });
}
